import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Divider,
    Input,
    InputOnChangeData,
    makeStyles,
    MessageBarBody,
    MessageBarTitle,
    shorthands
} from '@fluentui/react-components';
import { ProtocolContractStages } from '../../Protocols/ProtocolContractForm/ProtocolContractStages/ProtocolContractStages';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';

// component functions
import * as ComponentFunctions from './StageInitialForm.Functions';
import { NewContractStage } from '../../../../models/Entities/Contracts/NewContractStage';
import { NewStageCycle } from '../../../../models/Entities/Contracts/NewStageCycle';

////i18Next
import { useTranslation } from 'react-i18next';

const useStageInitialFormStyles = makeStyles({
    errorList: {
        ...shorthands.margin(0),
    },
});

interface FormValidation {
success: boolean;
errors: string[];
}

// Step 1: Define the debounce function
const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

export const ContractStageInitialFormComponent = (props: ComponentFunctions.Props) => {

const styles = useStageInitialFormStyles();
const [submittedForm, setSubmittedForm] = useState<boolean>(false);
const [FormValidationStatus, setFormValidationStatus] = useState<
    FormValidation | undefined
>();

// entity properties
const [numberOfStages, setNumberOfStages] = useState<number>(0);
const [deboncedNumberOfStages, setDeboncedNumberOfStages] = useState<number>(0);
const [stageName, setStageName] = useState<string>('Visita');
const [cycleName, setCycleName] = useState<string>('Ciclo');
const [cycleAmount, setCycleAmount] = useState<number>(0);
const [currentStages, setCurrentStages] = useState<NewContractStage[]>([]);
const [currentCycles, setCurrentCycles] = useState<NewStageCycle[]>([]);

//useTranslation()
const[t] = useTranslation();

const ResetForm = () => {
    setNumberOfStages(0);
    setStageName('Visita');
    setCycleName('Ciclo');
    setCycleAmount(0);
    setCurrentStages([]);
    setCurrentCycles([]);
    setDeboncedNumberOfStages(0);
};

useEffect(() => {
    if (submittedForm && props.AddRangeFinalized) {
        ResetForm();
        props.dismissPanel();
    }
}, [submittedForm, props.AddRangeFinalized]);


// Events

const onChangeStageNameTextFieldValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
) => {
    data.value ? setStageName(data.value) : setStageName('');
};

const onChangecycleNameTextFieldValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
) => {
    data.value ? setCycleName(data.value) : setCycleName('');
};

const onChangeNumberOfStagesTextFieldValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
) => {
    data.value ? setNumberOfStages(Number(data.value)) : setNumberOfStages(0);
    onChangeDebouncedNumberOfStagesTextFieldValue(event, data.value);
};

const onChangeDebouncedNumberOfStagesTextFieldValue = useCallback(
    debounce((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
) => {
    newValue ? setDeboncedNumberOfStages(Number(newValue)) : setDeboncedNumberOfStages(0);
}, 400), []);

const onChangeCycleAmountTextFieldValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
    ) => {
        data.value ? setCycleAmount(Number(data.value)) : setCycleAmount(0);
    }

const HandleSaveForm = async () => {
        if (props.isAdding) return;
    setSubmittedForm(true);
    if (ValidateForm()) {
      //props.AddContractStageCycleRange(props.ContractId, props.AddendumId, currentCycles as NewStageCycle[]);
      props.AddContractStageRange(props.ContractId, props.AddendumId, currentStages as NewContractStage[], currentCycles as NewStageCycle[]);
    }
};

const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
};

const formFields = (
    <>
        <GuideField
            label={t('stageInitialForm.props.visitName')}
            required
            error={
            (submittedForm === true && stageName === undefined) ||
            (submittedForm === true &&
                stageName !== undefined &&
                stageName.length === 0)
                ?  t('stageInitialForm.props.requiredField')
                : undefined
            }
        >
            <Input
                name="txt_stage_name"
                type="text"
                autoComplete="off"
                onFocus={(e) => e.currentTarget.select()}
                onChange={onChangeStageNameTextFieldValue}
                value={stageName || ''}
            />
        </GuideField>
        <GuideField
            label={t('stageInitialForm.props.visitNumber')}
            required
            error={
            (submittedForm === true && numberOfStages === undefined) ||
            (submittedForm === true &&
                numberOfStages !== undefined &&
                numberOfStages === 0)
                ? t('stageInitialForm.props.requiredField')
                : undefined
            }
        >
            <Input
                name="txt_number_of_stages"
                type="number"
                autoComplete="off"
                onFocus={(e) => e.currentTarget.select()}
                onChange={onChangeNumberOfStagesTextFieldValue}
                value={numberOfStages.toString() || ''}
            />
        </GuideField>
        <GuideField
            label={t('stageInitialForm.props.cycleName')}
            error={
            (submittedForm === true && cycleName === undefined) ||
            (submittedForm === true &&
                cycleName !== undefined &&
                cycleName.length === 0)
                ? t('stageInitialForm.props.requiredField')
                : undefined
            }
        >
            <Input
                name="txt_cycle_name"
                type="text"
                autoComplete="off"
                onFocus={(e) => e.currentTarget.select()}
                onChange={onChangecycleNameTextFieldValue}
                value={cycleName || ''}
            />
        </GuideField>
        <GuideField label={t('stageInitialForm.props.cycleNumber')}>
            <Input
                name="txt_number_of_cycles"
                type="number"
                autoComplete="off"
                onFocus={(e) => e.currentTarget.select()}
                onChange={onChangeCycleAmountTextFieldValue}
                value={cycleAmount.toString() || ''}
            />
        </GuideField>
        <Divider />
        <ProtocolContractStages
            onChangeStages={(stages: NewContractStage[]) => {console.log(stages), setCurrentStages(stages)}}
            onChangeCycles={(cycles: NewStageCycle[]) => setCurrentCycles(cycles)}
            currentStages={[]}
            currentCycles={[]}
            stageName={stageName}
            stageAmount={deboncedNumberOfStages}
            CycleAmount={cycleAmount}
            CycleName={cycleName}
        />
    </>

);

// form validation

const ValidateForm = (): boolean => {
    let validationResult: boolean = true;

    let FormValidation: FormValidation = { success: true, errors: [] };
    setFormValidationStatus(FormValidation);

    if (stageName === undefined || stageName.length === 0) {
    FormValidation.errors.push(t('stageInitialForm.props.requiredVisitName'));
    validationResult = false;
    };

    if (numberOfStages === undefined || numberOfStages === 0) {
        FormValidation.errors.push(t('stageInitialForm.props.requiredVisitNumber'));
        validationResult = false;
    };

    FormValidation.success = validationResult;
    setFormValidationStatus(FormValidation);
    return validationResult;
};

return (
    <GuideDrawer
        open={props.isOpen}
        size="medium"
        title={t('stageInitialForm.panel.visitInformation')}
        onDismiss={props.dismissPanel}
        secondaryAction={{
            label: t('stageInitialForm.footer.cancelButton'),
            onClick: HandleCancelForm,
            disabled: props.isAdding
        }}
        primaryAction={{
            label: t('stageInitialForm.footer.saveButton'),
            onClick: HandleSaveForm,
            loading: props.isAdding
        }}
        testId="stage-initial-form-drawer"
    >
    {submittedForm && !FormValidationStatus?.success && (
        <GuideMessageBar intent="error">
            <MessageBarBody>
                <MessageBarTitle>{t('stageInitialForm.panel.reviewInformation')}</MessageBarTitle>
                <ul className={styles.errorList}>
                    {FormValidationStatus?.errors.map(
                    (Error: string, index: number) => (
                        <li key={index}>{Error}</li>
                    )
                    )}
                </ul>
            </MessageBarBody>
        </GuideMessageBar>
    )}
    {props.failOnAdding &&
    props.error && (
        <ErrorMessageComponent Error={props.error} />
    )}
    {formFields}
    </GuideDrawer>
);
};

export default connect(
ComponentFunctions.mapStateToProps,
ComponentFunctions.mapDispatchToProps
)(ContractStageInitialFormComponent as any);
